/**
 * Task tab — work item hierarchy, dependencies, linked KB, comments.
 *
 * Data loaded on-demand from fetchWorkItemDetail().
 */

import { esc } from '../../../shared/rendering.js'
import { fetchWorkItemDetail } from '../../../api/work.js'
import type { WorkItemDetail } from '../../../types/api.js'

/** Render the Task tab content. */
export function renderTaskTab(workItemId: string): string {
  // Render placeholder, then load data async
  setTimeout(() => {
    void loadTaskData(workItemId)
  }, 0)

  return `<div id="task-tab-content"><div class="loading"><div class="spinner"></div></div></div>`
}

async function loadTaskData(workItemId: string): Promise<void> {
  const container = document.getElementById('task-tab-content')
  if (!container) {
    return
  }

  try {
    const detail = await fetchWorkItemDetail(workItemId)
    container.innerHTML = renderWorkItemDetail(detail)
  } catch (err) {
    container.innerHTML = `<div class="empty">Failed to load work item. ${esc(String(err))}</div>`
  }
}

function renderWorkItemDetail(item: WorkItemDetail): string {
  // Breadcrumb
  const breadcrumb = item.ancestors
    .map((a) => `<span style="color:var(--t4);cursor:pointer">${esc(a.id)} ${esc(a.title)}</span>`)
    .concat([
      `<span style="color:var(--cyn);font-weight:600">${esc(item.id)} ${esc(item.title)}</span>`,
    ])
    .join(' <span style="color:var(--t4)">&rsaquo;</span> ')

  // Hierarchy
  const hierarchyItems = [
    ...item.ancestors.map(
      (a, i) =>
        `<div style="display:flex;align-items:center;gap:6px;padding:4px 0 4px ${i * 18}px;cursor:pointer">
        <span style="font-family:var(--mono);font-size:10px;color:var(--t4)">${esc(a.id)}</span>
        <span style="font-size:12px;color:var(--t2)">${esc(a.title)}</span>
        <span style="font-family:var(--mono);font-size:9px;padding:1px 5px;border-radius:3px;background:var(--s3);color:var(--t3);margin-left:auto">${esc(a.type)}</span>
      </div>`,
    ),
    `<div style="display:flex;align-items:center;gap:6px;padding:5px 8px 5px ${item.ancestors.length * 18}px;background:var(--cyn-d);border-radius:4px;margin:2px 0">
      <span style="font-family:var(--mono);font-size:10px;color:var(--cyn)">${esc(item.id)}</span>
      <span style="font-size:12px;font-weight:500;color:var(--t1)">${esc(item.title)}</span>
      <span style="font-family:var(--mono);font-size:9px;padding:1px 5px;border-radius:3px;background:var(--cyn-d);color:var(--cyn);margin-left:auto">${esc(item.type)} \u00b7 ${esc(item.status)}</span>
    </div>`,
    ...item.siblings
      .filter((s) => s.id !== item.id)
      .map(
        (s) =>
          `<div style="display:flex;align-items:center;gap:6px;padding:4px 0 4px ${item.ancestors.length * 18}px;cursor:pointer;opacity:.6">
        <span style="font-family:var(--mono);font-size:10px;color:var(--t4)">${esc(s.id)}</span>
        <span style="font-size:12px;color:var(--t3)">${esc(s.title)}</span>
        <span style="font-family:var(--mono);font-size:9px;padding:1px 5px;border-radius:3px;background:var(--s3);color:var(--t3);margin-left:auto">${esc(s.status)}</span>
      </div>`,
      ),
  ].join('')

  // Dependencies
  const depsHtml =
    item.dependencies.length > 0
      ? item.dependencies
          .map(
            (d) =>
              `<div style="display:flex;align-items:center;gap:8px;padding:5px 10px;background:var(--s1);border:1px solid var(--bd);border-radius:6px;margin-bottom:4px">
            <span style="font-family:var(--mono);font-size:10px;color:var(--t3)">${esc(d.id)}</span>
            <span style="font-size:12px;flex:1">${esc(d.title)}</span>
            <span style="font-family:var(--mono);font-size:9px;padding:1px 6px;border-radius:3px;background:var(--grn-d);color:var(--grn)">${esc(d.status)}</span>
          </div>`,
          )
          .join('')
      : '<div style="font-size:12px;color:var(--t3)">No dependencies</div>'

  // Linked KB
  const kbHtml =
    item.linked_kb.length > 0
      ? item.linked_kb
          .map(
            (k) =>
              `<div style="display:flex;align-items:center;gap:8px;padding:5px 10px;background:var(--s1);border:1px solid var(--bd);border-left:3px solid var(--grn);border-radius:6px;margin-bottom:4px">
            <span style="font-family:var(--mono);font-size:9px;padding:1px 5px;border-radius:3px;background:var(--grn-d);color:var(--grn)">${esc(k.type ?? 'kb')}</span>
            <span style="font-size:12px;flex:1">${esc(k.title ?? k.id)}</span>
          </div>`,
          )
          .join('')
      : '<div style="font-size:12px;color:var(--t3)">No linked KB entries</div>'

  // Comments
  const commentsHtml =
    item.comments.length > 0
      ? item.comments
          .map((c) => {
            const isAgent = c.is_agent
            const borderStyle = isAgent ? 'border-left:3px solid var(--cyn);' : ''
            const author = isAgent ? '\u{1F43B} Grizzly Agent' : (c.author ?? 'Unknown')
            return `<div style="padding:10px 12px;background:var(--s1);border:1px solid var(--bd);${borderStyle}border-radius:8px;margin-bottom:6px">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
              <span style="font-size:12px;font-weight:500">${esc(author)}</span>
              <span style="font-family:var(--mono);font-size:10px;color:var(--t4)">${esc(c.posted_at ?? '')}</span>
            </div>
            <div style="font-size:12px;color:var(--t2);line-height:1.6">${esc(c.body)}</div>
          </div>`
          })
          .join('')
      : '<div style="font-size:12px;color:var(--t3)">No comments</div>'

  return `
    <div style="display:flex;align-items:center;gap:4px;margin-bottom:14px;font-family:var(--mono);font-size:11px;flex-wrap:wrap">${breadcrumb}</div>
    <div style="background:var(--s1);border:1px solid var(--bd);border-radius:8px;padding:12px 14px;margin-bottom:14px">
      <div style="font-family:var(--mono);font-size:10px;font-weight:600;color:var(--t3);text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px">Work Item Hierarchy</div>
      ${hierarchyItems}
    </div>
    ${item.description ? `<div style="font-size:12px;color:var(--t2);line-height:1.7;margin-bottom:14px">${esc(item.description)}</div>` : ''}
    <div class="sl">Dependencies</div>
    <div style="margin-bottom:14px">${depsHtml}</div>
    <div class="sl">Linked KB</div>
    <div style="margin-bottom:14px">${kbHtml}</div>
    <div class="sl">Comments</div>
    ${commentsHtml}`
}
